import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit2, Link2, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { useParkingModule } from '../hooks/useParkingModule';
import { Modal } from '../components/Modal';

interface RuleRow {
  id: number;
  spot_type: string;
  unit: string;
  price_per_unit: number;
  currency: string;
  is_active: boolean;
}

const UNITS = ['day', 'night'] as const;

export function ParkingPricingPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { activeMarinaId } = useAuth();
  const { parkingEnabled, loading: modLoading } = useParkingModule();
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editRule, setEditRule] = useState<RuleRow | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<RuleRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const toastRef = useRef(toast);
  toastRef.current = toast;

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getParkingPricingRules();
      setRules((data.rules ?? []) as unknown as RuleRow[]);
    } catch (err) {
      if (err instanceof ApiError) toastRef.current.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeMarinaId]);

  useEffect(() => {
    if (parkingEnabled) fetchRules();
    else setLoading(false);
  }, [fetchRules, parkingEnabled]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.deleteParkingPricingRule(deleteTarget.id);
      toast.success(t('parkingPricing.deleted'));
      setDeleteTarget(null);
      fetchRules();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    } finally {
      setDeleting(false);
    }
  };

  if (modLoading || (parkingEnabled && loading)) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!parkingEnabled) {
    return (
      <div className="max-w-lg space-y-3">
        <h1 className="text-xl font-bold text-gray-900">{t('parkingPricing.title')}</h1>
        <p className="text-sm text-gray-600">{t('parkingSpots.moduleDisabled')}</p>
        <RouterLink to="/modules" className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700">
          <Link2 className="w-4 h-4" /> {t('parkingSpots.openModules')}
        </RouterLink>
      </div>
    );
  }

  const activeRules = rules.filter((r) => r.is_active);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">{t('parkingPricing.title')}</h1>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
        >
          <Plus className="w-4 h-4" /> {t('parkingPricing.addRule')}
        </button>
      </div>
      <p className="text-sm text-gray-500">{t('parkingPricing.subtitle')}</p>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {activeRules.length === 0 ? (
          <div className="px-5 py-16 text-center text-sm text-gray-400">
            {t('parkingPricing.noRules')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('parkingSpots.spotType')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('parkingPricing.unit')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('parkingPricing.pricePerUnit')}</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activeRules.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900">{t(`parkingSpots.types.${r.spot_type}` as Parameters<typeof t>[0])}</td>
                    <td className="px-4 py-3 text-gray-600">{t(`parkingPricing.units.${r.unit}` as Parameters<typeof t>[0], { defaultValue: r.unit })}</td>
                    <td className="px-4 py-3 text-gray-900">{Number(r.price_per_unit).toFixed(2)} {r.currency}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => setEditRule(r)} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600" aria-label={t('parkingPricing.editTitle')}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" onClick={() => setDeleteTarget(r)} className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500" aria-label={t('parkingPricing.deleteRule')}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PricingRuleModal
        open={showAdd || !!editRule}
        onClose={() => { setShowAdd(false); setEditRule(null); }}
        initial={editRule}
        existing={rules}
        onSaved={() => { setShowAdd(false); setEditRule(null); fetchRules(); }}
      />

      <Modal open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} title={t('parkingPricing.deleteRule')} maxWidth="max-w-md">
        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            {deleteTarget
              ? t('parkingPricing.deleteConfirm', {
                  vehicle: t(`parkingSpots.types.${deleteTarget.spot_type}` as Parameters<typeof t>[0]),
                  billing: t(`parkingPricing.units.${deleteTarget.unit}` as Parameters<typeof t>[0], { defaultValue: deleteTarget.unit }),
                })
              : null}
          </p>
          <div className="flex gap-2">
            <button type="button" disabled={deleting} onClick={() => setDeleteTarget(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">
              {t('common.cancel')}
            </button>
            <button
              type="button"
              disabled={deleting}
              onClick={() => { void confirmDelete(); }}
              className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {deleting ? t('common.loading') : t('common.delete')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function PricingRuleModal({
  open,
  onClose,
  initial,
  existing,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  initial: RuleRow | null;
  existing: RuleRow[];
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [spotType, setSpotType] = useState('car');
  const [unit, setUnit] = useState<string>('day');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setSpotType(initial.spot_type);
        setUnit(initial.unit);
        setPrice(String(initial.price_per_unit));
        setCurrency(initial.currency);
      } else {
        setSpotType('car');
        setUnit('day');
        setPrice('');
        setCurrency('EUR');
      }
    }
  }, [open, initial]);

  const violatesUnique = (excludeId?: number): boolean =>
    existing.some(
      (r) =>
        r.spot_type === spotType &&
        r.unit === unit &&
        (excludeId === undefined || r.id !== excludeId),
    );

  const submit = async () => {
    const p = parseFloat(price.replace(',', '.'));
    if (!Number.isFinite(p) || p < 0) {
      toast.error(t('parkingPricing.invalidPrice'));
      return;
    }

    const excludeId = initial?.id;
    if (violatesUnique(excludeId)) {
      toast.error(t('parkingPricing.duplicateRule'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        spot_type: spotType,
        unit,
        price_per_unit: p,
        currency: currency.slice(0, 3).toUpperCase(),
      };
      if (initial) {
        await api.updateParkingPricingRule(initial.id, payload);
        toast.success(t('parkingPricing.updated'));
      } else {
        await api.createParkingPricingRule(payload);
        toast.success(t('parkingPricing.created'));
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'DUPLICATE') {
        toast.error(t('parkingPricing.duplicateRule'));
      } else {
        toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={initial ? t('parkingPricing.editTitle') : t('parkingPricing.addTitle')} maxWidth="max-w-md">
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('parkingSpots.spotType')}</label>
          <select
            value={spotType}
            onChange={(e) => setSpotType(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {(['car', 'caravan', 'trailer'] as const).map((st) => (
              <option key={st} value={st}>{t(`parkingSpots.types.${st}` as Parameters<typeof t>[0])}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('parkingPricing.unit')}</label>
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {UNITS.map((u) => (
              <option key={u} value={u}>{t(`parkingPricing.units.${u}` as Parameters<typeof t>[0])}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('parkingPricing.pricePerUnit')} *</label>
            <input value={price} onChange={(e) => setPrice(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="0.00" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('parkingPricing.currency')}</label>
            <input value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase" />
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">{t('common.cancel')}</button>
          <button type="button" disabled={saving} onClick={() => { void submit(); }} className="flex-1 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
